import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Modal,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { Children, useEffect, useState } from 'react';
import { User } from 'src/interfaces/iuser';
import ModeEditOutlineRoundedIcon from '@mui/icons-material/ModeEditOutlineRounded';
import DoneRoundedIcon from '@mui/icons-material/DoneRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { COLOR_GREEN, COLOR_PRIMARY, COLOR_RED } from 'src/utils/const';
import {
  COLOR_GREY_200,
  COLOR_GREY_400,
  COLOR_GREY_800,
  COLOR_PRIMARY_2,
  COLOR_TEXT_1,
  COLOR_TEXT_2,
  COLOR_TEXT_3,
  COLOR_TEXT_GREY,
} from 'src/utils/colors';
import { callApi } from 'src/core/callApi';
import { clearApiCache } from 'src/utils/functions';
import { logoutThunk } from 'src/store/Auth/actions';
import { useDispatch } from 'react-redux';
import { unsubscribeUser } from './helper';
import TooltipNew from 'src/App/Dashboards/Market/Components/Tooltips/TooltipNew';

const TextElement = ({
  title,
  value,
  initialValue,
  disabled,
  loading,
  setValueOnChange,
  handleUpdate,
  styles,
}: {
  title: string;
  value: string | number;
  initialValue: string | number;
  disabled: boolean;
  setValueOnChange?: React.Dispatch<
    React.SetStateAction<{
      initial: string | number;
      edited: string | number;
      updating: boolean;
    }>
  >;
  handleUpdate?: () => Promise<void>;
  loading?: boolean;
  styles: any;
}) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  return (
    <Grid container sx={styles}>
      <Grid item xs={12} sx={{ mb: '10px' }}>
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          {title}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <TextField
          label=""
          variant="outlined"
          value={value}
          sx={{ width: '300px' }}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setValueOnChange &&
              isEditing &&
              setValueOnChange(prev => ({
                ...prev,
                edited: event.target.value,
              }));
          }}
          InputProps={{
            ...(!disabled
              ? {
                  endAdornment: isEditing ? (
                    <InputAdornment position="end">
                      <Box sx={{ display: 'flex', gap: '5px' }}>
                        <IconButton
                          onClick={() => {
                            setIsEditing(false);
                            handleUpdate && handleUpdate();
                          }}
                        >
                          <DoneRoundedIcon sx={{ color: COLOR_GREEN }} />
                        </IconButton>
                        <IconButton
                          onClick={() => {
                            setIsEditing(false);
                            setValueOnChange &&
                              setValueOnChange(prev => ({
                                ...prev,
                                edited: initialValue,
                              }));
                          }}
                        >
                          <CloseRoundedIcon sx={{ color: COLOR_RED }} />
                        </IconButton>
                      </Box>
                    </InputAdornment>
                  ) : (
                    <InputAdornment position="end">
                      {loading ? (
                        <CircularProgress />
                      ) : (
                        <IconButton onClick={() => setIsEditing(true)}>
                          <ModeEditOutlineRoundedIcon />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                }
              : {}),
            readOnly: isEditing ? false : true,
          }}
        />
      </Grid>
    </Grid>
  );
};

const ProfileTab = ({ user }: { user: User }) => {
  const [userName, setUserName] = useState<{
    initial: string | number;
    edited: string | number;
    updating: boolean;
  }>({
    initial: user.userName,
    edited: user.userName,
    updating: false,
  });
  const [notification, setNotification] = useState<any>(null);
  const [unsubscriptionModalOpen, setUnsubscriptionModalOpen] = useState<boolean>(false);
  const [userUnSubscriptionStatus, setUserUnSubscriptionStatus] = useState<
    'unsubscribed' | 'subscribed' | 'updating'
  >('unsubscribed');
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [isAcountDeleting, setIsAccountDeleting] = useState<boolean>(false);

  const dispatch = useDispatch();

  useEffect(() => {
    if (user.email !== 'Trial') {
      setUserName({
        initial: user.userName,
        edited: user.userName,
        updating: false,
      });
      user.isSubscribedToNewsletter && setUserUnSubscriptionStatus('subscribed');
    }
  }, [user]);

  const handleNameUpdate = async () => {
    const URL = 'https://fld2j6mes8.execute-api.us-east-2.amazonaws.com/default/updateUserDetails';
    setUserName(prev => ({
      ...prev,
      updating: true,
    }));
    const response = await callApi(
      URL,
      {
        queryParams: {
          name: userName.edited,
        },
      },
      true,
      false,
      '',
      'PUT',
      true
    );

    if (response.error) {
      setUserName(prev => ({
        ...prev,
        updating: false,
      }));
      setNotification(
        <Alert
          onClose={() => setNotification(null)}
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          Update Failed!
        </Alert>
      );
    } else {
      setUserName(prev => ({
        ...prev,
        initial: prev.edited,
        updating: false,
      }));
    }
  };

  const handleEmailMarketingUnsubscription = async () => {
    setUnsubscriptionModalOpen(false);
    setUserUnSubscriptionStatus('updating');
    const URL =
      'https://fn7crazpf6.execute-api.us-east-2.amazonaws.com/default/handleMailChimpOpertations';
    const response = await callApi(URL, {}, true, false, '', 'DELETE', true);

    if (response.error) {
      setNotification(
        <Alert
          onClose={() => setNotification(null)}
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {response.error}
        </Alert>
      );
    } else {
      setUserUnSubscriptionStatus('unsubscribed');
      setNotification(
        <Alert
          onClose={() => setNotification(null)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          Successfully unsubscribed from receiving emails!
        </Alert>
      );
    }
  };

  const handleAccountDelete = async () => {
    setIsAccountDeleting(true);

    try {
      if (user.isSubscribedToNewsletter) {
        const isUnsubscribed = await unsubscribeUser();
        isUnsubscribed &&
          setNotification(
            <Alert
              onClose={() => setNotification(null)}
              severity="success"
              variant="filled"
              sx={{ width: '100%' }}
            >
              Successfully unsubscribed your current subscription!
            </Alert>
          );
      }

      const queryParams = new URLSearchParams({
        deletedUser: user.email,
        deletedBy: 'user',
      });

      const URL = `https://n4k7t590h1.execute-api.us-east-2.amazonaws.com/default/deleteUserFromDynamoDB?${queryParams.toString()}`;
      const response = await callApi(URL, {}, true, false, '', 'DELETE', true);

      if (response.error && response.error?.message) {
        setNotification(
          <Alert
            onClose={() => setNotification(null)}
            severity="error"
            variant="filled"
            sx={{ width: '100%' }}
          >
            {response.error.message}
          </Alert>
        );
      } else {
        // Log the user out
        dispatch(logoutThunk());
        await clearApiCache();
        window.location.href = '/';
      }
    } catch (err) {
      console.log('Error: ', err);
      setNotification(
        <Alert
          onClose={() => setNotification(null)}
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          Something went wrong while deleting your account.
        </Alert>
      );
    }
    setIsAccountDeleting(false);
  };

  if (user.email === 'Trial') {
    return (
      <Box
        sx={{
          width: '100%',
          height: '200px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <>
      <Grid container sx={{ mt: '20px', gap: '20px' }}>
        <TooltipNew content="You cannot change your email from this page. Please contact support for assistance with updating your email.">
          <span>
            <TextElement
              title="Email"
              value={user.email}
              initialValue={user.email}
              disabled={true}
              styles={{ width: '300px' }}
            />
          </span>
        </TooltipNew>
        <TextElement
          title="Name"
          value={userName.edited}
          initialValue={userName.initial}
          loading={userName.updating}
          setValueOnChange={setUserName}
          handleUpdate={() => handleNameUpdate()}
          disabled={false}
          styles={{ width: '300px' }}
        />
      </Grid>
      <Grid container sx={{ mt: '30px' }}>
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            Password
          </Typography>
        </Grid>

        <Grid item xs={12} sx={{ mt: '10px' }}>
          <Typography variant="body1" sx={{ color: COLOR_TEXT_2, mr: '10px' }}>
            Can't remember your current password?{' '}
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                window.open('/reset-password');
              }}
              sx={{ color: '#fff' }}
            >
              Reset Your Password
            </Button>
          </Typography>
        </Grid>
      </Grid>

      {['subscribed', 'updating'].includes(userUnSubscriptionStatus) && (
        <Grid container sx={{ mt: '30px' }}>
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              Communication preference
            </Typography>
          </Grid>

          <Grid item xs={12} sx={{ mt: '10px' }}>
            <Typography variant="body1" sx={{ color: COLOR_TEXT_2 }}>
              Email Marketing{' '}
              <Button
                variant="contained"
                size="small"
                onClick={() => setUnsubscriptionModalOpen(true)}
                sx={{ ml: '10px', color: '#fff' }}
              >
                {userUnSubscriptionStatus === 'updating' ? (
                  <CircularProgress sx={{ color: '#fff' }} size={20} />
                ) : (
                  'Unsubscribe'
                )}
              </Button>
            </Typography>
          </Grid>
        </Grid>
      )}

      <Divider sx={{ width: '100%', mt: '30px', mb: '20px' }} />

      <Grid container>
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            Delete your account
          </Typography>
        </Grid>
        <Typography variant="body1" sx={{ color: COLOR_TEXT_2, mt: '10px' }}>
          Click here to delete your account:{' '}
          <Button
            variant="contained"
            size="small"
            onClick={() => setDeleteModalOpen(true)}
            sx={{
              ml: '10px',
              background: COLOR_GREY_800,
              color: COLOR_TEXT_GREY,
              '&:hover': {
                background: COLOR_RED,
                color: '#fff',
              },
            }}
          >
            {isAcountDeleting ? <CircularProgress size={20} /> : <>Delete Account</>}
          </Button>
        </Typography>
      </Grid>

      {notification && (
        <Snackbar
          open={Boolean(notification)}
          autoHideDuration={3000}
          onClose={() => setNotification(null)}
        >
          {notification}
        </Snackbar>
      )}

      {unsubscriptionModalOpen && (
        <EmailUnsubscriptionConfirmationModal
          title="Are you sure you want to unsubscribe from receiving emails?"
          handleOnClose={() => setUnsubscriptionModalOpen(false)}
          handleOnConfirm={handleEmailMarketingUnsubscription}
        />
      )}

      {deleteModalOpen && (
        <DeleteAccountConfirmationModal
          user={user}
          handleOnClose={() => {
            setDeleteModalOpen(false);
          }}
          handleAccountDelete={handleAccountDelete}
        />
      )}
    </>
  );
};

const ModalWrapper = ({ children, handleOnClose }) => {
  return (
    <Modal
      open={true}
      onClose={handleOnClose}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          border: 'none',
          outline: 'none',
          width: 'clamp(300px, 100%, 600px)',
          padding: '30px',
          borderRadius: '12px',
          background: '#fff',
          position: 'relative',
        }}
      >
        <IconButton
          onClick={handleOnClose}
          sx={{
            position: 'absolute',
            top: '4px',
            right: '4px',
          }}
        >
          <CloseRoundedIcon sx={{ color: COLOR_RED }} />
        </IconButton>
        <>{children}</>
      </Box>
    </Modal>
  );
};

const EmailUnsubscriptionConfirmationModal = ({ title, handleOnClose, handleOnConfirm }) => {
  return (
    <ModalWrapper handleOnClose={handleOnClose}>
      <>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: '20px' }}>
          {title}
        </Typography>
        <Grid container justifyContent={'flex-end'} sx={{ gap: '10px' }}>
          <Button
            onClick={handleOnClose}
            sx={{ color: COLOR_TEXT_GREY, background: COLOR_GREY_800 }}
          >
            Cancel
          </Button>
          <Button onClick={handleOnConfirm} sx={{ color: '#fff', background: COLOR_PRIMARY }}>
            Unsubscribe
          </Button>
        </Grid>
      </>
    </ModalWrapper>
  );
};

const DeleteAccountConfirmationModal = ({
  user,
  handleOnClose,
  handleAccountDelete,
}: {
  user: User;
  handleOnClose: () => void;
  handleAccountDelete: () => void;
}) => {
  const [uiViewIndex, setUiViewIndex] = useState<number>(0);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState<string>('');

  const DELETE_CONFIRMATION_TEXT = 'DELETE MY ACCOUNT';

  const isAccountDeleteReady = deleteConfirmationText === DELETE_CONFIRMATION_TEXT;

  const UiViewFirstConfirmation = (
    <>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: '20px' }}>
        Are you sure you want to delete your account?
      </Typography>
      <Grid container justifyContent={'flex-end'} sx={{ gap: '10px' }}>
        <Button
          variant="contained"
          onClick={() => setUiViewIndex(1)}
          sx={{
            color: COLOR_TEXT_GREY,
            background: COLOR_GREY_800,
            '&:hover': {
              background: COLOR_RED,
              color: '#fff',
            },
          }}
        >
          Delete
        </Button>
        <Button
          variant="contained"
          onClick={handleOnClose}
          sx={{ color: '#fff', background: COLOR_PRIMARY }}
        >
          Keep My Account
        </Button>
      </Grid>
    </>
  );

  const UiViewFinalConfirmation = (
    <>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: '20px' }}>
        To confirm, type <span style={{ color: COLOR_TEXT_GREY }}>{DELETE_CONFIRMATION_TEXT}</span>.
        This will permanently remove your account and all associated data.
      </Typography>
      <Grid container sx={{ my: '20px' }}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder={'Type ' + DELETE_CONFIRMATION_TEXT}
            onChange={event => setDeleteConfirmationText(event.target.value)}
            error={isAccountDeleteReady ? false : true}
          />
        </Grid>
      </Grid>
      <Grid container justifyContent={'flex-end'} sx={{ gap: '10px' }}>
        <Button
          variant="contained"
          onClick={() => {
            handleAccountDelete();
            handleOnClose();
          }}
          disabled={isAccountDeleteReady ? false : true}
          sx={{
            color: COLOR_TEXT_GREY,
            background: COLOR_GREY_800,
            '&:hover': {
              background: COLOR_RED,
              color: '#fff',
            },
          }}
        >
          Delete
        </Button>
        <Button
          variant="contained"
          onClick={handleOnClose}
          sx={{ color: '#fff', background: COLOR_PRIMARY }}
        >
          Keep My Account
        </Button>
      </Grid>
      <Grid container justifyContent={'center'}>
        <Typography variant="body2" textAlign={'center'} sx={{ mt: '20px', color: COLOR_GREY_400 }}>
          * Your account will be permanently deleted within 7 days.
        </Typography>
      </Grid>
    </>
  );

  return (
    <ModalWrapper handleOnClose={handleOnClose}>
      {uiViewIndex === 0 && UiViewFirstConfirmation}
      {uiViewIndex === 1 && UiViewFinalConfirmation}
    </ModalWrapper>
  );
};

export default ProfileTab;
